import * as React from 'react';

// Type imports (internal first, then external)
import type { AddedAnalyticsProps } from '@helpcenter/analytics-components/src/with-create-fire-ui-analytics';
import { isMultiSelectEnabled, isLabelsSupportEnabled } from '@helpcenter/feature-flags';
import { ThemedSelect, ThemedAsyncSelect, ThemedAsyncCreatableSelect } from 'view/common/themed-select';

import { withControlledFormComponentValue } from 'view/common/with-controlled-form-component-value';
import type { SelectProps, Option } from '@atlaskit/select';
import { componentWithCondition } from '@atlassian/help-center-common-util/feature-flags-utils';

import type { SelectFieldFormProps } from '../with-field-base';
import { withFieldBase } from '../with-field-base';

const FieldMultiSelect = withFieldBase(ThemedSelect);
const FieldAsyncCreatableMultiSelect = withFieldBase(
    ThemedAsyncCreatableSelect as React.FunctionComponent<SelectProps<Option<string>, true>>
);
const FieldAsyncMultiSelect = withFieldBase(
    ThemedAsyncSelect as React.FunctionComponent<SelectProps<Option<string>, true>>
);

// We remove the value prop here so that the interface is consistent with the single select. The value prop also does not what
// you expect it to do as you will need to handle the state of the select component yourself if provided
type PropsWithField = Omit<SelectProps<Option<string>>, 'value'> &
    Omit<SelectFieldFormProps, 'value'> &
    AddedAnalyticsProps;

const FormMultiSelectWithControlledValue = withControlledFormComponentValue<Option<string>[]>(
    (options: Option<string>[]) => options
)((props: PropsWithField) => <FieldMultiSelect {...props} isMulti isClearable />);

const FormMultiSelect = componentWithCondition(
    isMultiSelectEnabled,
    FormMultiSelectWithControlledValue,
    (props: PropsWithField) => <FieldMultiSelect {...props} isMulti isClearable />
);

const FormAsyncMultiSelect: React.FunctionComponent<PropsWithField> = (props) => (
    <FieldAsyncMultiSelect {...props} isMulti isClearable />
);
const FormAsyncCreatableMultiSelectUncontrolled: React.FunctionComponent<PropsWithField> = (props) => (
    <FieldAsyncCreatableMultiSelect {...props} isMulti isClearable />
);

const FormAsyncCreatableMultiSelectControlled = withControlledFormComponentValue<Option<string>[]>(
    (options: Option<string>[]) => options
)(FormAsyncCreatableMultiSelectUncontrolled);

const FormAsyncCreatableMultiSelect = componentWithCondition(
    () => isLabelsSupportEnabled(),
    FormAsyncCreatableMultiSelectControlled,
    FormAsyncCreatableMultiSelectUncontrolled
);

export { FormMultiSelect, FormAsyncMultiSelect, FormAsyncCreatableMultiSelect };
